// Collection Header V2 — Media carousel, Quicklinks carousel, Countdown timer

// ── Media Carousel ──────────────────────────────────────────────────────────

class Ch2Carousel {
  constructor(wrapper) {
    this.carousel = wrapper.querySelector('[data-ch2-carousel]');
    if (!this.carousel) return;

    this.items = Array.from(this.carousel.querySelectorAll('.ch2-carousel__item'));
    if (this.items.length === 0) return;

    this.prevBtn  = wrapper.querySelector('.ch2-carousel__btn--prev');
    this.nextBtn  = wrapper.querySelector('.ch2-carousel__btn--next');
    this.dotsWrap = wrapper.querySelector('[data-ch2-dots]');
    this.dots     = this.dotsWrap ? Array.from(this.dotsWrap.querySelectorAll('.ch2-carousel__dot')) : [];
    this.current  = 0;

    this.prevBtn && this.prevBtn.addEventListener('click', () => this.go(this.current - 1));
    this.nextBtn && this.nextBtn.addEventListener('click', () => this.go(this.current + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
  }

  go(index) {
    const len = this.items.length;
    this.current = ((index % len) + len) % len; // loop
    this.items.forEach((item, i) => item.classList.toggle('active', i === this.current));
    this.dots.forEach((dot, i) => dot.classList.toggle('active', i === this.current));
  }
}

// ── Quicklinks Carousel ─────────────────────────────────────────────────────

class Ch2QuicklinksCarousel {
  constructor(el) {
    this.track   = el.querySelector('[data-ch2-ql-track]');
    this.prevBtn = el.querySelector('.ch2-ql__arrow--prev');
    this.nextBtn = el.querySelector('.ch2-ql__arrow--next');
    if (!this.track) return;

    this.prevBtn && this.prevBtn.addEventListener('click', () => this.prev());
    this.nextBtn && this.nextBtn.addEventListener('click', () => this.next());

    this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
    window.addEventListener('resize', () => this.updateArrows());
    this.updateArrows();
  }

  scrollStep() {
    const firstItem = this.track.querySelector('.ch2-ql__link');
    if (!firstItem) return 200;
    return firstItem.offsetWidth + parseInt(getComputedStyle(this.track).gap || '0', 10);
  }

  updateArrows() {
    if (!this.prevBtn || !this.nextBtn) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const fitsAll = max <= 1;

    this.prevBtn.classList.toggle('ch2-ql__arrow--hidden', fitsAll);
    this.nextBtn.classList.toggle('ch2-ql__arrow--hidden', fitsAll);
    this.prevBtn.classList.toggle('ch2-ql__arrow--disabled', !fitsAll && this.track.scrollLeft <= 4);
    this.nextBtn.classList.toggle('ch2-ql__arrow--disabled', !fitsAll && this.track.scrollLeft >= max - 4);
  }

  next() {
    this.track.scrollBy({ left: this.scrollStep(), behavior: 'smooth' });
  }

  prev() {
    this.track.scrollBy({ left: -this.scrollStep(), behavior: 'smooth' });
  }
}

// ── Equal-height columns ─────────────────────────────────────────────────────
// .ch2-left spans multiple grid row tracks (it covers the media-footer row
// too), so `align-items: stretch` sizes it against both rows combined, not
// just against .ch2-right — the two columns can end up mismatched. Instead,
// explicitly size .ch2-right to match .ch2-left's rendered height in JS.

function ch2SyncMediaHeight(body) {
  const left  = body.querySelector('.ch2-left');
  const right = body.querySelector('.ch2-right');
  if (!left || !right) return;

  // With a footer, .ch2-left spans both grid rows (media row + footer row),
  // so its rendered height covers the footer too — sizing .ch2-right to
  // match would stretch it well past the media it's meant to bound. Leave
  // .ch2-right alone (the CSS min-height floor governs) while the footer is
  // actually on screen.
  const footer = body.querySelector('.ch2-media-footer');
  if (footer && footer.offsetParent !== null) {
    right.style.height = '';
    return;
  }

  right.style.height = left.getBoundingClientRect().height + 'px';
}

function ch2SyncAllMediaHeights() {
  queryAll(document, '.collection-header-v2 .ch2-body').forEach((body) => {
    if (!body.classList.contains('ch2-body--expanded')) ch2SyncMediaHeight(body);
  });
}

// ── Read More ────────────────────────────────────────────────────────────────

class Ch2ReadMore {
  constructor(el) {
    this.el             = el;
    this.collapsedHeight = parseInt(el.dataset.ch2CollapsedHeight, 10) || 200;
    this.heightMargin    = parseInt(el.dataset.ch2HeightMargin, 10) || 16;
    this.speed           = parseInt(el.dataset.ch2Speed, 10) || 100;
    this.breakpoint      = parseInt(el.dataset.ch2Breakpoint, 10) || 0;
    this.moreLabel       = el.dataset.ch2MoreLabel || 'Read more';
    this.lessLabel       = el.dataset.ch2LessLabel || 'Close';

    this.expanded = false;
    this.button   = null;
    this.body     = this.el.closest('.ch2-body');
    this.mediaRight = this.body ? this.body.querySelector('.ch2-right') : null;

    this.el.style.transition = `height ${this.speed}ms ease-in-out`;

    // .ch2-left's rendered height only reflects the collapsed description
    // once its height transition has actually finished animating — syncing
    // right after setting the collapsed height (synchronously) would still
    // measure the pre-collapse (expanded) height and lock .ch2-right to the
    // wrong, too-tall value. Wait for the transition to settle instead.
    this.el.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'height' && !this.expanded && this.body) {
        ch2SyncMediaHeight(this.body);
      }
    });

    if (this.breakpoint) {
      this.media = window.matchMedia(`(max-width: ${this.breakpoint}px)`);
      this.media.addEventListener('change', () => this.refresh());
      this.refresh();
    } else {
      this.enable();
    }
  }

  refresh() {
    if (this.media.matches) this.enable();
    else this.disable();
  }

  enable() {
    if (this.button) return;

    this.el.style.overflow = 'hidden';
    this.el.style.height   = this.collapsedHeight + 'px';

    this.button = document.createElement('div');
    this.button.className = 'readmore-button';
    this.button.innerHTML = `<a href="#">${this.moreLabel}</a>`;
    this.el.parentNode.insertBefore(this.button, this.el.nextSibling);

    if (this.el.scrollHeight <= this.collapsedHeight) {
      this.button.style.display = 'none';
    }

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      this.expanded = !this.expanded;
      if (this.expanded) {
        // Pin the media column to its current (synced, equal) height before
        // growing the description, so it doesn't grow to match it too.
        if (this.mediaRight) {
          this.mediaRight.style.height = this.mediaRight.getBoundingClientRect().height + 'px';
        }
        if (this.body) this.body.classList.add('ch2-body--expanded');
        this.el.style.height = (this.el.scrollHeight + this.heightMargin) + 'px';
        this.button.innerHTML = `<a href="#">${this.lessLabel}</a>`;
      } else {
        this.el.style.height = this.collapsedHeight + 'px';
        this.button.innerHTML = `<a href="#">${this.moreLabel}</a>`;
        if (this.body) this.body.classList.remove('ch2-body--expanded');
        // The transitionend listener re-syncs .ch2-right once this height
        // change has finished animating (see constructor).
      }
    });
  }

  disable() {
    if (!this.button) return;
    this.button.remove();
    this.button = null;
    this.el.style.height = '';
    this.expanded = false;
    if (this.body) {
      this.body.classList.remove('ch2-body--expanded');
      ch2SyncMediaHeight(this.body);
    }
  }
}

// ── Quicklinks: highlight current page ──────────────────────────────────────

function ch2HighlightCurrentQuicklinks(scope) {
  const current = window.location.pathname + window.location.search;
  queryAll(scope, '.js-keep-params-v2').forEach(link => {
    const u = new URL(link.href, window.location.origin);
    link.classList.toggle('current', (u.pathname + u.search) === current);
  });
}

// ── Countdown Timer ──────────────────────────────────────────────────────────

class Ch2Countdown {
  constructor(el) {
    const raw = el.dataset.ch2Countdown;
    if (!raw) return;

    this.el       = el;
    this.end      = new Date(raw).getTime();
    this.daysEl   = el.querySelector('[data-ch2-days]');
    this.hoursEl  = el.querySelector('[data-ch2-hours]');
    this.minsEl   = el.querySelector('[data-ch2-minutes]');
    this.secsEl   = el.querySelector('[data-ch2-seconds]');

    if (isNaN(this.end)) return;
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  pad(n) {
    return String(n).padStart(2, '0');
  }

  tick() {
    // The theme editor can replace this section's markup without tearing this
    // instance down first — stop ticking once its element is detached.
    if (!document.body.contains(this.el)) {
      clearInterval(this.interval);
      return;
    }

    const diff = this.end - Date.now();
    if (diff <= 0) {
      clearInterval(this.interval);
      [this.daysEl, this.hoursEl, this.minsEl, this.secsEl].forEach(el => { if (el) el.textContent = '00'; });
      return;
    }
    const days  = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins  = Math.floor((diff % 3600000) / 60000);
    const secs  = Math.floor((diff % 60000) / 1000);
    if (this.daysEl)  this.daysEl.textContent  = this.pad(days);
    if (this.hoursEl) this.hoursEl.textContent = this.pad(hours);
    if (this.minsEl)  this.minsEl.textContent  = this.pad(mins);
    if (this.secsEl)  this.secsEl.textContent  = this.pad(secs);
  }
}

// ── Init ─────────────────────────────────────────────────────────────────────

// Includes `scope` itself when it matches, not just its descendants — needed
// because the Shopify theme editor's `shopify:section:load` target can be the
// matching element itself rather than an ancestor wrapper.
function queryAll(scope, selector) {
  const results = Array.from(scope.querySelectorAll(selector));
  if (scope.matches && scope.matches(selector)) results.unshift(scope);
  return results;
}

function initCollectionHeaderV2(scope) {
  queryAll(scope, '.collection-header-v2 .ch2-right').forEach(wrapper => {
    new Ch2Carousel(wrapper);
  });

  queryAll(scope, '[data-ch2-quicklinks]').forEach(el => {
    new Ch2QuicklinksCarousel(el);
  });

  queryAll(scope, '[data-ch2-countdown]').forEach(el => {
    new Ch2Countdown(el);
  });

  queryAll(scope, '[data-ch2-readmore]').forEach(el => {
    new Ch2ReadMore(el);
  });

  // Runs after the readmore instances above so the description is already
  // collapsed to its resting height before .ch2-right is measured against it.
  queryAll(scope, '.collection-header-v2 .ch2-body').forEach(body => {
    ch2SyncMediaHeight(body);
  });

  ch2HighlightCurrentQuicklinks(scope);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCollectionHeaderV2(document));
} else {
  initCollectionHeaderV2(document);
}

// The theme editor replaces a section's markup via innerHTML on every setting
// change, which never re-executes <script> tags — re-init explicitly here so
// the read more button, carousels, and countdown survive customizer edits.
document.addEventListener('shopify:section:load', (event) => {
  initCollectionHeaderV2(event.target);
});

// Re-sync on resize (column width changes reflow the text and its height),
// and once more after web fonts / images finish loading, since those can
// shift .ch2-left's height after the first (pre-load) measurement.
let ch2ResizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(ch2ResizeTimer);
  ch2ResizeTimer = setTimeout(ch2SyncAllMediaHeights, 150);
});

window.addEventListener('load', ch2SyncAllMediaHeights);

if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(ch2SyncAllMediaHeights);
}
